import React, { useMemo } from 'react';

type AreaChartProps = {
  width?: number;
  height?: number;
  values: number[];
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
  strokeWidth?: number;
};

export const AreaChart: React.FC<AreaChartProps> = ({ width = 420, height = 120, values, color = '#384df4', gradientFrom = '#384df420', gradientTo = '#384df404', strokeWidth = 2 }) => {
  const { path, area, min, max } = useMemo(() => {
    if (!values || values.length === 0) return { path: '', area: '', min: 0, max: 1 };
    const pad = 8;
    const w = width;
    const h = height;
    const vmax = Math.max(1, ...values);
    const vmin = Math.min(0, ...values);
    const scaleX = (i: number) => pad + (i * (w - 2 * pad)) / Math.max(1, values.length - 1);
    const scaleY = (v: number) => h - pad - ((v - vmin) / Math.max(1e-6, (vmax - vmin))) * (h - 2 * pad);
    const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(v)}`).join(' ');
    const area = `M ${pad} ${h - pad} ${values.map((v, i) => `L ${scaleX(i)} ${scaleY(v)}`).join(' ')} L ${w - pad} ${h - pad} Z`;
    return { path, area, min: vmin, max: vmax };
  }, [values, width, height]);

  const id = useMemo(() => `grad-${Math.random().toString(36).slice(2)}`, []);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none" role="img" aria-label="Area chart">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={gradientFrom} />
          <stop offset="100%" stopColor={gradientTo} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  );
};

type DonutSegment = { label: string; value: number; color: string };
export const DonutChart: React.FC<{ size?: number; thickness?: number; segments: DonutSegment[]; centerLabel?: string; subLabel?: string; }> = ({ size = 160, thickness = 16, segments, centerLabel, subLabel }) => {
  const total = Math.max(1e-6, segments.reduce((a, s) => a + (s.value || 0), 0));
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  let cumulative = 0;
  const circles = segments.map((s, idx) => {
    const frac = s.value / total;
    const dash = 2 * Math.PI * r * frac;
    const gap = 2 * Math.PI * r * (1 - frac);
    const rot = cumulative * 360;
    cumulative += frac;
    return (
      <circle key={idx} r={r} cx={cx} cy={cy} fill="transparent" stroke={s.color} strokeWidth={thickness}
        strokeDasharray={`${dash} ${gap}`} transform={`rotate(-90 ${cx} ${cy}) rotate(${rot} ${cx} ${cy})`}>
        <title>{`${s.label}: ${s.value}`}</title>
      </circle>
    );
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Donut chart">
      {circles}
      <circle r={r} cx={cx} cy={cy} fill="none" stroke="#e2e8f0" strokeWidth={1} />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={Math.max(12, size*0.16)} fontWeight={700} fill="#0f172a">{centerLabel ?? (Math.round(total)).toString()}</text>
      { (subLabel || '') !== '' && (
        <text x={cx} y={cy + size*0.16} textAnchor="middle" dominantBaseline="middle" fontSize={Math.max(10, size*0.09)} fill="#64748b">{subLabel}</text>
      )}
    </svg>
  );
};

export const Gauge: React.FC<{ size?: number; value: number; max?: number; color?: string; }> = ({ size = 180, value, max = 1, color = '#22c55e' }) => {
  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = Math.PI; // 180deg
  const endAngle = Math.PI + Math.PI; // 360deg (semi)
  const pct = Math.max(0, Math.min(1, value / max));
  const angle = startAngle + pct * Math.PI;
  const x = cx + r * Math.cos(angle);
  const y = cy + r * Math.sin(angle);
  const arc = describeArc(cx, cy, r, 180, 360);
  return (
    <svg width={size} height={size/2} viewBox={`0 0 ${size} ${size/2}`} role="img" aria-label="Gauge">
      <path d={arc} stroke="#e2e8f0" strokeWidth={12} fill="none" />
      <path d={describeArc(cx, cy, r, 180, 180 + pct * 180)} stroke={color} strokeWidth={12} fill="none" />
      <circle cx={x} cy={y} r={6} fill={color} />
      <text x={cx} y={(size/2)-8} textAnchor="middle" fontSize={12} fill="#64748b">{Math.round(value)}h / {Math.round(max)}h</text>
    </svg>
  );
};

function describeArc(cx: number, cy: number, r: number, start: number, end: number) {
  const startRad = (Math.PI/180) * start;
  const endRad = (Math.PI/180) * end;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  const largeArc = end - start <= 180 ? '0' : '1';
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

export const StackedBar: React.FC<{ width?: number; height?: number; parts: { value: number; color: string; }[]; }> = ({ width = 420, height = 16, parts }) => {
  const total = Math.max(1e-6, parts.reduce((a, p) => a + (p.value || 0), 0));
  let x = 0;
  const rects = parts.map((p, i) => {
    const w = (p.value / total) * width;
    const rect = <rect key={i} x={x} y={0} width={Math.max(0, w)} height={height} fill={p.color}><title>{p.value}</title></rect>;
    x += w;
    return rect;
  });
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Stacked bar">
      <rect x={0} y={0} width={width} height={height} fill="#f1f5f9" />
      {rects}
      <rect x={0} y={0} width={width} height={height} fill="none" stroke="#e2e8f0" />
    </svg>
  );
};

// Ultra-simple, game-like widgets
export const TrafficLight: React.FC<{ state: 'green'|'yellow'|'red'; size?: number; labels?: { green?: string; yellow?: string; red?: string } }> = ({ state, size = 18, labels }) => {
  const circle = (color: string, active: boolean, title?: string) => (
    <span title={title} style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      background: color, opacity: active ? 1 : 0.25, boxShadow: active ? `0 0 10px ${color}` : 'none',
      marginRight: 6,
    }} />
  );
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {circle('#22c55e', state==='green', labels?.green)}
      {circle('#f59e0b', state==='yellow', labels?.yellow)}
      {circle('#ef4444', state==='red', labels?.red)}
    </span>
  );
};

export const BlockMeter: React.FC<{ totalBlocks?: number; value: number; max: number; color?: string; size?: number; }> = ({ totalBlocks = 10, value, max, color = '#384df4', size = 12 }) => {
  const pct = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  const filled = Math.round(pct * totalBlocks);
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {Array.from({ length: totalBlocks }).map((_, i) => (
        <span key={i} title={`${Math.round(pct*100)}%`} style={{ width: size, height: size, borderRadius: 3, background: i < filled ? color : '#e2e8f0' }} />
      ))}
    </span>
  );
};

export const StepBar: React.FC<{ steps: { label: string; days: number; color: string; }[]; width?: number; height?: number; }> = ({ steps, width = 420, height = 16 }) => {
  const total = Math.max(1e-6, steps.reduce((a, s) => a + (s.days || 0), 0));
  let x = 0;
  const rects = steps.map((s, i) => {
    const w = (s.days / total) * width;
    const rect = <rect key={i} x={x} y={0} width={Math.max(0, w)} height={height} fill={s.color}><title>{`${s.label}: ${s.days}d`}</title></rect>;
    x += w;
    return rect;
  });
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="Step bar timeline">
      <rect x={0} y={0} width={width} height={height} fill="#f8fafc" />
      {rects}
      <rect x={0} y={0} width={width} height={height} fill="none" stroke="#e2e8f0" />
    </svg>
  );
};
